import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    FormControl,
    FormLabel,
    Input,
    Select,
    Textarea,
    IconButton,
    Box,
    useToast,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Flex,
    Spacer,
    Text,
    useDisclosure,
    ButtonGroup
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import * as ReactDOM from 'react-dom/client';

// Custom EditableTitle component to avoid nesting issues
const EditableTitle = ({ value, onChange, index }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value);

    const handleClick = (e) => {
        e.stopPropagation();
        setIsEditing(true);
    };

    const handleBlur = () => {
        setIsEditing(false);
        onChange(tempValue);
    };

    const handleKeyDown = (e) => {
        e.stopPropagation();
        if (e.key === 'Enter' || e.key === 'Escape') {
            e.currentTarget.blur();
        }
    };

    if (isEditing) {
        return (
            <Input
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                size="sm"
                width="100%"
                maxWidth="600px"
                onClick={(e) => e.stopPropagation()}
                autoFocus
            />
        );
    }

    return (
        <Text
            onClick={handleClick}
            cursor="pointer"
            _hover={{ bg: "gray.100", px: 2, mx: -2, borderRadius: "md" }}
            display="inline-block"
            width="100%"
            px={2}
            mx={-2}
        >
            {value || `Entry ${index + 1}`}
        </Text>
    );
};

const ReleaseFormModal = ({ isOpen, onClose, onSuccess, editRelease = null }) => {
    const supabase = useSupabaseClient();
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [expandedIndexes, setExpandedIndexes] = useState([0]);
    const [releases, setReleases] = useState([]);
    const [selectedEntryIndex, setSelectedEntryIndex] = useState(null);
    const { isOpen: isMoveModalOpen, onOpen: openMoveModal, onClose: closeMoveModal } = useDisclosure();

    // Release form state
    const [release, setRelease] = useState({
        version_major: 1,
        version_minor: 0,
        version_patch: 0,
        release_name: '',
        release_date: new Date().toISOString().split('T')[0],
        status: 'PLANNED',
        release_notes: ''
    });

    // Changelog entries state
    const newEntryTemplate = {
        title: '',
        description: '',
        category_id: '',
        change_type: 'MINOR',
        impact_level: 'MEDIUM',
        status: 'PLANNED'
    };

    const [entries, setEntries] = useState([newEntryTemplate]);

    // Load categories on mount
    useEffect(() => {
        fetchCategories();
    }, []);

    // Load existing data when editing
    useEffect(() => {
        if (editRelease) {
            setRelease({
                version_major: editRelease.version_major,
                version_minor: editRelease.version_minor,
                version_patch: editRelease.version_patch,
                release_name: editRelease.release_name,
                release_date: new Date(editRelease.release_date).toISOString().split('T')[0],
                status: editRelease.status,
                release_notes: editRelease.release_notes
            });
            
            if (editRelease.changelog_entries?.length > 0) {
                setEntries(editRelease.changelog_entries.map(entry => ({
                    id: entry.id,
                    category_id: entry.category_id,
                    change_type: entry.change_type,
                    title: entry.title,
                    description: entry.description,
                    impact_level: entry.impact_level,
                    status: entry.status
                })));
            }
        }
    }, [editRelease]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            if (!editRelease) {
                setRelease({
                    version_major: 1,
                    version_minor: 0,
                    version_patch: 0,
                    release_name: '',
                    release_date: new Date().toISOString().split('T')[0],
                    status: 'PLANNED',
                    release_notes: ''
                });
                setEntries([newEntryTemplate]);
            }
        }
    }, [isOpen]);

    const fetchCategories = async () => {
        try {
            const { data, error } = await supabase
                .from('changelog_categories')
                .select('*')
                .order('name');

            if (error) throw error;
            setCategories(data || []);
        } catch (err) {
            console.error('Error fetching categories:', err);
            toast({
                title: 'Error fetching categories',
                description: err.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleReleaseChange = (field, value) => {
        setRelease(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleEntryChange = (index, field, value) => {
        const updatedEntries = [...entries];
        updatedEntries[index] = {
            ...updatedEntries[index],
            [field]: value
        };
        setEntries(updatedEntries);
    };

    const addEntry = () => {
        setEntries(prev => [...prev, newEntryTemplate]);
    };

    const removeEntry = (index) => {
        setEntries(prev => prev.filter((_, i) => i !== index));
    };

    const moveEntryToRelease = async (entryIndex) => {
        try {
            const { data, error: fetchError } = await supabase
                .from('releases')
                .select('id, release_name, version_major, version_minor, version_patch')
                .order('created_at', { ascending: false });

            if (fetchError) throw fetchError;
            
            setReleases(data);
            setSelectedEntryIndex(entryIndex);
            openMoveModal();
        } catch (error) {
            console.error('Error fetching releases:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch releases',
                status: 'error',
            });
        }
    };

    const handleMoveEntry = async (selectedRelease) => {
        try {
            const entry = entries[selectedEntryIndex];
            
            // Move the entry to the selected release
            const { error: moveError } = await supabase
                .from('changelog_entries')
                .update({ release_id: selectedRelease.id })
                .eq('id', entry.id);

            if (moveError) throw moveError;

            // Remove entry from current list
            const updatedEntries = entries.filter((_, i) => i !== selectedEntryIndex);
            setEntries(updatedEntries);

            toast({
                title: 'Entry moved',
                description: `Entry moved to release ${selectedRelease.release_name}`,
                status: 'success',
            });

            closeMoveModal();
        } catch (error) {
            console.error('Error moving entry:', error);
            toast({
                title: 'Error',
                description: 'Failed to move entry',
                status: 'error',
            });
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            // Validate required fields
            if (!release.release_name || !release.release_date) {
                throw new Error('Please fill in all required fields');
            }

            if (entries.some(entry => !entry.category_id || !entry.description)) {
                throw new Error('Please fill in all changelog entry fields');
            }

            if (editRelease) {
                // Update existing release
                const { error: releaseError } = await supabase
                    .from('releases')
                    .update({
                        version_major: release.version_major,
                        version_minor: release.version_minor,
                        version_patch: release.version_patch,
                        release_name: release.release_name,
                        release_date: release.release_date,
                        status: release.status,
                        release_notes: release.release_notes
                    })
                    .eq('id', editRelease.id);

                if (releaseError) throw releaseError;

                // Handle existing entries
                const existingEntryIds = entries
                    .filter(entry => entry.id)
                    .map(entry => entry.id);

                // Delete removed entries
                if (existingEntryIds.length > 0) {
                    const { error: deleteError } = await supabase
                        .from('changelog_entries')
                        .delete()
                        .eq('release_id', editRelease.id)
                        .not('id', 'in', `(${existingEntryIds.join(',')})`);

                    if (deleteError) throw deleteError;
                }

                // Update existing entries and insert new ones
                for (const entry of entries) {
                    const entryData = {
                        release_id: editRelease.id,
                        category_id: entry.category_id,
                        change_type: entry.change_type,
                        title: entry.title,
                        description: entry.description,
                        impact_level: entry.impact_level,
                        status: entry.status
                    };

                    if (entry.id) {
                        // Update existing entry
                        const { error: updateError } = await supabase
                            .from('changelog_entries')
                            .update(entryData)
                            .eq('id', entry.id);

                        if (updateError) throw updateError;
                    } else {
                        // Insert new entry
                        const { error: insertError } = await supabase
                            .from('changelog_entries')
                            .insert([entryData]);

                        if (insertError) throw insertError;
                    }
                }

                toast({
                    title: 'Release updated',
                    description: 'The release and changelog entries have been updated successfully',
                    status: 'success',
                    duration: 5000,
                    isClosable: true
                });
            } else {
                // Insert new release
                const { data: releaseData, error: releaseError } = await supabase
                    .from('releases')
                    .insert([release])
                    .select()
                    .single();

                if (releaseError) throw releaseError;

                // Insert changelog entries
                const entriesWithReleaseId = entries.map(entry => ({
                    ...entry,
                    release_id: releaseData.id
                }));

                const { error: entriesError } = await supabase
                    .from('changelog_entries')
                    .insert(entriesWithReleaseId);

                if (entriesError) throw entriesError;

                toast({
                    title: 'Release created',
                    description: 'The release and changelog entries have been created successfully',
                    status: 'success',
                    duration: 5000,
                    isClosable: true
                });
            }

            onSuccess?.();
            onClose();
        } catch (error) {
            toast({
                title: editRelease ? 'Error updating release' : 'Error creating release',
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent maxW="1200px">
                    <ModalHeader>{editRelease ? 'Edit Release' : 'Create New Release'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={6} align="stretch">
                            {/* Release Information Section */}
                            <Box 
                                p={4} 
                                borderWidth="2px"
                                borderColor="gray.300"
                                borderRadius="lg"
                                bg="white"
                            >
                                <Text fontSize="xl" fontWeight="bold" mb={4} color="blue.700">
                                    Release Information
                                </Text>
                                <VStack spacing={4}>
                                    {/* Version and Status */}
                                    <HStack width="100%" spacing={4} align="flex-end">
                                        <Box width="40%">
                                            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>Version</Text>
                                            <HStack spacing={2}>
                                                <FormControl width="80px">
                                                    <FormLabel color="gray.600" fontSize="xs">Major</FormLabel>
                                                    <NumberInput
                                                        value={release.version_major}
                                                        onChange={(value) => handleReleaseChange('version_major', parseInt(value))}
                                                        min={0}
                                                    >
                                                        <NumberInputField bg="white" />
                                                        <NumberInputStepper>
                                                            <NumberIncrementStepper />
                                                            <NumberDecrementStepper />
                                                        </NumberInputStepper>
                                                    </NumberInput>
                                                </FormControl>
                                                <FormControl width="80px">
                                                    <FormLabel color="gray.600" fontSize="xs">Minor</FormLabel>
                                                    <NumberInput
                                                        value={release.version_minor}
                                                        onChange={(value) => handleReleaseChange('version_minor', parseInt(value))}
                                                        min={0}
                                                    >
                                                        <NumberInputField bg="white" />
                                                        <NumberInputStepper>
                                                            <NumberIncrementStepper />
                                                            <NumberDecrementStepper />
                                                        </NumberInputStepper>
                                                    </NumberInput>
                                                </FormControl>
                                                <FormControl width="80px">
                                                    <FormLabel color="gray.600" fontSize="xs">Patch</FormLabel>
                                                    <NumberInput
                                                        value={release.version_patch}
                                                        onChange={(value) => handleReleaseChange('version_patch', parseInt(value))}
                                                        min={0}
                                                    >
                                                        <NumberInputField bg="white" />
                                                        <NumberInputStepper>
                                                            <NumberIncrementStepper />
                                                            <NumberDecrementStepper />
                                                        </NumberInputStepper>
                                                    </NumberInput>
                                                </FormControl>
                                            </HStack>
                                        </Box>
                                        <FormControl flex={1}>
                                            <FormLabel color="gray.600">Status</FormLabel>
                                            <Select
                                                value={release.status}
                                                onChange={(e) => handleReleaseChange('status', e.target.value)}
                                                bg="white"
                                            >
                                                <option value="PLANNED">Planned</option>
                                                <option value="IN_DEVELOPMENT">In Development</option>
                                                <option value="READY">Ready</option>
                                                <option value="PUBLISHED">Published</option>
                                                <option value="DEPRECATED">Deprecated</option>
                                            </Select>
                                        </FormControl>
                                    </HStack>

                                    {/* Name and Date */}
                                    <HStack width="100%" spacing={4}>
                                        <FormControl flex={2}>
                                            <FormLabel color="gray.600">Release Name</FormLabel>
                                            <Input
                                                value={release.release_name}
                                                onChange={(e) => handleReleaseChange('release_name', e.target.value)}
                                                placeholder="e.g., Winter Update 2025"
                                                bg="white"
                                            />
                                        </FormControl>
                                        <FormControl flex={1}>
                                            <FormLabel color="gray.600">Release Date</FormLabel>
                                            <Input
                                                type="date"
                                                value={release.release_date}
                                                onChange={(e) => handleReleaseChange('release_date', e.target.value)}
                                                bg="white"
                                            />
                                        </FormControl>
                                    </HStack>

                                    <FormControl>
                                        <FormLabel color="gray.600">Release Notes</FormLabel>
                                        <Textarea
                                            value={release.release_notes}
                                            onChange={(e) => handleReleaseChange('release_notes', e.target.value)}
                                            placeholder="General notes about this release..."
                                            rows={3}
                                            bg="white"
                                        />
                                    </FormControl>
                                </VStack>
                            </Box>

                            {/* Changelog Entries Section */}
                            <Box>
                                <Flex justify="space-between" align="center" mb={4}>
                                    <Text fontSize="xl" fontWeight="bold" color="blue.700">
                                        Changelog Entries
                                    </Text>
                                    <HStack spacing={2}>
                                        <ButtonGroup size="sm" spacing={2}>
                                            <Button
                                                leftIcon={<ChevronDownIcon />}
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setExpandedIndexes(entries.map((_, i) => i))}
                                            >
                                                Expand All
                                            </Button>
                                            <Button
                                                leftIcon={<ChevronUpIcon />}
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setExpandedIndexes([])}
                                            >
                                                Collapse All
                                            </Button>
                                        </ButtonGroup>
                                        <Button
                                            size="sm"
                                            leftIcon={<AddIcon />}
                                            onClick={addEntry}
                                            colorScheme="blue"
                                        >
                                            Add Entry
                                        </Button>
                                    </HStack>
                                </Flex>

                                <Box
                                    borderWidth="2px"
                                    borderColor="gray.300"
                                    borderRadius="lg"
                                    bg="white"
                                    p={4}
                                >
                                    <Accordion 
                                        allowMultiple 
                                        index={expandedIndexes}
                                        onChange={setExpandedIndexes}
                                    >
                                        {entries.map((entry, index) => (
                                            <AccordionItem 
                                                key={index}
                                                mb={2}
                                                border="none"
                                            >
                                                <Box
                                                    borderWidth="1px"
                                                    borderColor="gray.300"
                                                    borderRadius="md"
                                                    bg="white"
                                                    boxShadow="sm"
                                                >
                                                    <AccordionButton 
                                                        _expanded={{ bg: 'blue.50', color: 'blue.700' }}
                                                        borderRadius="md"
                                                        p={3}
                                                    >
                                                        <Box flex="1" textAlign="left">
                                                            <EditableTitle
                                                                value={entry.title}
                                                                onChange={(value) => handleEntryChange(index, 'title', value)}
                                                                index={index}
                                                            />
                                                        </Box>
                                                        <HStack spacing={2}>
                                                            <IconButton
                                                                icon={<AddIcon />}
                                                                aria-label="Move to another release"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    moveEntryToRelease(index);
                                                                }}
                                                                title="Move to another release"
                                                                colorScheme="blue"
                                                                variant="ghost"
                                                                size="sm"
                                                            />
                                                            <IconButton
                                                                icon={<DeleteIcon />}
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeEntry(index);
                                                                }}
                                                                isDisabled={entries.length === 1}
                                                                aria-label="Delete entry"
                                                                colorScheme="red"
                                                            />
                                                            <AccordionIcon />
                                                        </HStack>
                                                    </AccordionButton>
                                                    <AccordionPanel pb={4} pt={2} px={4}>
                                                        <VStack spacing={4}>
                                                            <HStack width="100%" spacing={4} align="flex-end">
                                                                <FormControl flex={1}>
                                                                    <FormLabel color="gray.600" fontSize="sm">Category</FormLabel>
                                                                    <Select
                                                                        value={entry.category_id}
                                                                        onChange={(e) => handleEntryChange(index, 'category_id', e.target.value)}
                                                                        size="sm"
                                                                    >
                                                                        <option value="">Select a category</option>
                                                                        {categories.map(category => (
                                                                            <option key={category.id} value={category.id}>
                                                                                {category.name}
                                                                            </option>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                                <FormControl flex={1}>
                                                                    <FormLabel color="gray.600" fontSize="sm">Change Type</FormLabel>
                                                                    <Select
                                                                        value={entry.change_type}
                                                                        onChange={(e) => handleEntryChange(index, 'change_type', e.target.value)}
                                                                        size="sm"
                                                                    >
                                                                        <option value="MAJOR">Major</option>
                                                                        <option value="MINOR">Minor</option>
                                                                        <option value="PATCH">Patch</option>
                                                                    </Select>
                                                                </FormControl>
                                                                <FormControl flex={1}>
                                                                    <FormLabel color="gray.600" fontSize="sm">Impact Level</FormLabel>
                                                                    <Select
                                                                        value={entry.impact_level}
                                                                        onChange={(e) => handleEntryChange(index, 'impact_level', e.target.value)}
                                                                        size="sm"
                                                                    >
                                                                        <option value="HIGH">High</option>
                                                                        <option value="MEDIUM">Medium</option>
                                                                        <option value="LOW">Low</option>
                                                                    </Select>
                                                                </FormControl>
                                                                <FormControl flex={1}>
                                                                    <FormLabel color="gray.600" fontSize="sm">Status</FormLabel>
                                                                    <Select
                                                                        value={entry.status}
                                                                        onChange={(e) => handleEntryChange(index, 'status', e.target.value)}
                                                                        size="sm"
                                                                    >
                                                                        <option value="PLANNED">Planned</option>
                                                                        <option value="IN_PROGRESS">In Progress</option>
                                                                        <option value="COMPLETED">Completed</option>
                                                                    </Select>
                                                                </FormControl>
                                                            </HStack>

                                                            <FormControl>
                                                                <FormLabel color="gray.600">Description</FormLabel>
                                                                <Textarea
                                                                    value={entry.description}
                                                                    onChange={(e) => handleEntryChange(index, 'description', e.target.value)}
                                                                    placeholder="Detailed description of the change..."
                                                                    rows={2}
                                                                    bg="white"
                                                                />
                                                            </FormControl>
                                                        </VStack>
                                                    </AccordionPanel>
                                                </Box>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </Box>
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={handleSubmit}
                            isLoading={loading}
                        >
                            {editRelease ? 'Update Release' : 'Create Release'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Move Entry Modal */}
            <Modal isOpen={isMoveModalOpen} onClose={closeMoveModal}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Move to Release</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            {releases.map((rel) => (
                                <Button
                                    key={rel.id}
                                    width="100%"
                                    onClick={() => handleMoveEntry(rel)}
                                    variant="outline"
                                >
                                    {rel.release_name} (v{rel.version_major}.{rel.version_minor}.{rel.version_patch})
                                </Button>
                            ))}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={closeMoveModal}>Cancel</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default ReleaseFormModal;
